// Review marks for one PR: private to the user, stored on the server as
// content anchors (marks API version 2, docs/design.md "Review marks"),
// applied optimistically and queued (see marksQueue.ts).
//
// The queue holds one op per anchor (by `anchorId`): reviewed or not. A
// plan's unmarks and marks are applied in that order, so a merged anchor
// replaces the ones it absorbs.
//
// Unacknowledged writes are persisted one record per anchor
// (`<pr>|<anchor ID>`, see PendingMarkRecord), so tabs on the same PR merge
// rather than overwrite each other, and each tab deletes only the records it
// sent. They are restored once per store, and replayed for every PR when the
// app starts (`replayPendingMarks`); a key-based write an older client left
// is replayed through the key API.
import { isNotInstalled } from '../api/errors';
import { getMarksV2, putMarks, putMarksV2, resolveMarks, type MarksResolution, type MarksV2, type WireAnchor } from '../api/server';
import { idbDeleteIf, idbEntries, idbSet, type PendingMarkRecord } from '../api/db';
import { anchorId, fromWire, toWire, type Anchor, type Plan } from '../review/anchors';
import { Latest, type Outcome } from './latest';
import { MarksQueue, type PendingMarks, type PendingOp } from './marksQueue';

function prKey(owner: string, repo: string, number: number): string {
  return `${owner}/${repo}#${number}`.toLowerCase();
}

function recordKey(pr: string, id: string): string {
  return `${pr}|${id}`;
}

async function readPersisted(pr: string): Promise<{ pending: PendingMarks; anchors: Map<string, Anchor> }> {
  const ops: PendingMarks['ops'] = {};
  const anchors = new Map<string, Anchor>();
  for (const [, r] of await idbEntries('marks', pr + '|')) {
    // A key-based write from an older client: the startup replay sends it.
    if (r.pr !== pr || !r.anchor) continue;
    const a = fromWire(r.anchor);
    anchors.set(anchorId(a), a);
    ops[anchorId(a)] = { reviewed: r.reviewed, seq: 0, id: r.id };
  }
  return { pending: { ops }, anchors };
}

/** Queue persistence for one PR: one IndexedDB record per pending anchor. */
function persistence(pr: string, anchorOf: (id: string) => Anchor | undefined) {
  const written = new Map<string, string>(); // anchor ID → op ID already stored
  return {
    async persist(pending: PendingMarks) {
      for (const [id, op] of Object.entries(pending.ops)) {
        const anchor = anchorOf(id);
        if (!op.id || !anchor || written.get(id) === op.id) continue;
        written.set(id, op.id);
        const rec: PendingMarkRecord = { pr, block_key: id, anchor: toWire(anchor), reviewed: op.reviewed, id: op.id };
        await idbSet('marks', recordKey(pr, id), rec);
      }
    },
    async settled(ops: [string, PendingOp][]) {
      for (const [id, op] of ops) {
        if (written.get(id) === op.id) written.delete(id);
        await idbDeleteIf('marks', recordKey(pr, id), (r) => r.id === op.id);
      }
    },
  };
}

/** Stores of mounted PR views, so sign-out can send their queued writes first. */
const openStores = new Set<MarksStore>();

/** Send every open store's queued writes (one attempt each). */
export async function flushOpenMarks(): Promise<void> {
  await Promise.all([...openStores].map((s) => s.flush().catch(() => {})));
}

export class MarksStore {
  /** The reviewed anchors: the server's, with the writes it has not acknowledged on top. */
  anchors: readonly Anchor[] = $state.raw([]);
  /** Key-based marks the server holds as reviewed and not resolved yet (see `resolve`). */
  legacy: readonly string[] = $state.raw([]);
  loaded = $state(false);
  error: string | null = $state(null);
  /** The anchors written that the server has not acknowledged yet (shown as being saved). */
  unsent: readonly Anchor[] = $state.raw([]);
  private puts = $state(0);
  /** A PUT of marks is in flight. */
  get sending(): boolean {
    return this.puts > 0;
  }
  private queue: MarksQueue;
  /** The server's marks; a GET that a PUT's acknowledgement overtook is fetched again. */
  private remote: Latest<MarksV2>;
  /** The server's anchors as last read, by ID, reviewed or not. */
  private server = new Map<string, { anchor: Anchor; reviewed: boolean }>();
  /** Every anchor a queued op names, by ID. */
  private known = new Map<string, Anchor>();
  private key: string;
  private restored = false;
  private disposed = false;
  /** Every write of pending marks to IndexedDB started so far (see `persisted`). */
  private persisting: Promise<void> = Promise.resolve();

  /**
   * `onNotInstalled` is called, instead of reporting an error, when the
   * server answers 403 `not_installed`: it keeps no marks for the
   * repository (see `isNotInstalled`). `onSend` runs as each write of
   * marks is sent; what it returns runs once the write is answered,
   * accepted or failed.
   */
  constructor(
    private owner: string,
    private repo: string,
    private number: number,
    private onNotInstalled: () => void = () => {},
    private onSend: () => () => void = () => () => {},
  ) {
    this.key = prKey(owner, repo, number);
    openStores.add(this);
    const p = persistence(this.key, (id) => this.known.get(id));
    this.remote = new Latest(
      () => getMarksV2(owner, repo, number),
      (o) => this.applyRemote(o),
    );
    this.queue = new MarksQueue({
      send: async (marks) => {
        const anchors = marks.flatMap((m) => {
          const a = this.known.get(m.block_key);
          return a ? [{ ...toWire(a), reviewed: m.reviewed }] : [];
        });
        if (anchors.length) {
          this.puts++;
          const answered = this.onSend();
          try {
            await putMarksV2(owner, repo, number, anchors);
          } finally {
            this.puts--;
            answered();
          }
        }
        // A GET in flight may predate this write. Invalidated before the
        // queue lets go of the ops, so no stale GET applies without them.
        this.remote.invalidate();
      },
      persist: (pending) => {
        const done = p.persist(pending);
        this.persisting = Promise.all([this.persisting, done.catch(() => {})]).then(() => {});
        return done;
      },
      settled: async (ops) => {
        this.refreshUnsent();
        await p.settled(ops);
      },
      onError: (e, kind) => {
        if (this.disposed) return;
        if (isNotInstalled(e)) this.onNotInstalled();
        else if (kind === 'drop') {
          this.error = `Some review marks could not be saved: ${e instanceof Error ? e.message : String(e)}`;
          void this.load();
        } else if (kind === 'park') this.error = 'Sign in again to save review marks.';
      },
    });
  }

  /** Fetch the server's marks; of overlapping loads the latest applies (see `Latest`). */
  async load() {
    // Restore persisted writes once: later loads must not resurrect ops this
    // tab already sent (the server's `marks` notice follows every PUT).
    if (!this.restored) {
      this.restored = true;
      const { pending, anchors } = await readPersisted(this.key);
      for (const [id, a] of anchors) this.known.set(id, a);
      this.queue.restore(pending);
      this.recompute();
    }
    await this.remote.run();
  }

  private applyServer(v: MarksV2) {
    this.server = new Map(
      v.anchors.map((w) => {
        const anchor = fromWire(w);
        return [anchorId(anchor), { anchor, reviewed: w.reviewed }];
      }),
    );
    this.legacy = v.legacy.map((l) => l.block_key);
  }

  /** The server's marks, with the writes it has not acknowledged on top. */
  private applyRemote(o: Outcome<MarksV2>) {
    if (o.ok) {
      this.applyServer(o.value);
      this.error = null;
    } else if (isNotInstalled(o.error)) {
      this.onNotInstalled();
    } else {
      this.error = `Review marks could not be loaded: ${o.error instanceof Error ? o.error.message : String(o.error)}`;
    }
    this.recompute();
    this.loaded = true;
  }

  /** `anchors` and `unsent` from the server's marks and the queue. */
  private recompute() {
    const on = new Map<string, Anchor>();
    for (const [id, s] of this.server) if (s.reviewed) on.set(id, s.anchor);
    for (const [id, reviewed] of this.queue.pending()) {
      const a = this.known.get(id);
      if (!a) continue;
      if (reviewed) on.set(id, a);
      else on.delete(id);
    }
    this.anchors = [...on.values()];
    this.refreshUnsent();
  }

  private refreshUnsent() {
    this.unsent = [...this.queue.pending().keys()].flatMap((id) => {
      const a = this.known.get(id);
      return a ? [a] : [];
    });
  }

  /**
   * Remove the anchors in `plan.unset`, then store those in `plan.set` (a
   * plan from `review/model` `planLines` or `planFile`).
   *
   * Not atomic on the server: the queue coalesces by anchor (a later
   * write to an anchor replaces the one waiting, whichever plan it came
   * from) and sends MARKS_PER_PUT anchors per PUT, so a plan can straddle
   * two PUTs. A plan is a few anchors against thousands per PUT, so that
   * takes a backlog of thousands queued (e.g. offline). If one of the two
   * PUTs is then dropped (a 4xx, such as a cap), the marks are loaded
   * again, so the screen shows what the server holds, and the error says
   * so.
   */
  apply(plan: Plan) {
    // Stopped (`forget`, `dispose`): the server keeps nothing more from this
    // store, so nothing is shown or persisted to be replayed later.
    if (this.disposed || (!plan.set.length && !plan.unset.length)) return;
    for (const [list, reviewed] of [
      [plan.unset, false],
      [plan.set, true],
    ] as const) {
      for (const a of list) {
        const id = anchorId(a);
        this.known.set(id, { ...a });
        this.queue.set(id, reviewed);
      }
    }
    this.recompute();
  }

  /**
   * Record what this user's key-based marks cover (a client resolves them
   * once, see docs/design.md "Review marks"), and show the server's answer.
   * The server applies a key's resolution only while it is pending, so a
   * repeat, or another device's, changes nothing.
   */
  async resolve(body: MarksResolution): Promise<void> {
    if (this.disposed || (!body.resolved.length && !body.unresolvable.length)) return;
    // A GET in flight may predate the resolution.
    this.remote.invalidate();
    // A write of marks like any other: said as it is sent and answered.
    const answered = this.onSend();
    let v: MarksV2;
    try {
      v = await resolveMarks(this.owner, this.repo, this.number, body);
    } finally {
      answered();
    }
    if (this.disposed) return;
    this.applyServer(v);
    this.recompute();
  }

  get pendingWrites() {
    return this.queue.size;
  }

  flush() {
    return this.queue.flush();
  }

  /**
   * Resolves once every write of pending marks to IndexedDB started so far
   * has finished, stored or failed (each change is persisted as it is
   * made, apart from its PUT). For tests.
   */
  persisted(): Promise<void> {
    return this.persisting;
  }

  /** Stop (as `dispose`) and drop the marks shown: the server keeps none for this repository any more. */
  forget() {
    if (!this.disposed) this.dispose();
    this.server.clear();
    this.anchors = [];
    this.legacy = [];
    this.error = null;
  }

  /** Make one last attempt to send queued writes, then stop (see MarksQueue.dispose). */
  dispose() {
    this.disposed = true;
    this.remote.dispose();
    openStores.delete(this);
    void this.queue.flush();
    this.queue.dispose();
  }
}

/**
 * Send every persisted, unacknowledged mark write for every PR (run once the
 * user is known), so marks made in a tab that closed before its PUT landed
 * reach the server even if that PR is never opened again. Anchors go to the
 * marks API v2; a key-based write left by an older client goes to the key
 * API, which keeps it pending for resolution.
 */
export async function replayPendingMarks(): Promise<void> {
  const byPr = new Map<string, PendingMarkRecord[]>();
  for (const [, r] of await idbEntries('marks')) {
    const list = byPr.get(r.pr) ?? [];
    list.push(r);
    byPr.set(r.pr, list);
  }
  await Promise.all(
    [...byPr].map(async ([pr, records]) => {
      const m = /^(.+)\/(.+)#(\d+)$/.exec(pr);
      if (!m) return;
      const [, owner, repo, n] = m;
      const wire = new Map<string, WireAnchor>(records.flatMap((r) => (r.anchor ? [[r.block_key, r.anchor] as const] : [])));
      const p = persistence(pr, (id) => {
        const w = wire.get(id);
        return w && fromWire(w);
      });
      const q = new MarksQueue({
        send: async (marks) => {
          const anchors = marks.flatMap((x) => {
            const w = wire.get(x.block_key);
            return w ? [{ ...w, reviewed: x.reviewed }] : [];
          });
          const keys = marks.filter((x) => !wire.has(x.block_key));
          if (anchors.length) await putMarksV2(owner, repo, Number(n), anchors);
          if (keys.length) await putMarks(owner, repo, Number(n), keys);
        },
        persist: () => {},
        settled: p.settled,
      });
      q.restore({ ops: Object.fromEntries(records.map((r) => [r.block_key, { reviewed: r.reviewed, seq: 0, id: r.id }])) });
      await q.flush();
      q.dispose();
    }),
  );
}
